import { ReactNode, useEffect, useId, useState } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useDialogFocus } from '@/hooks/useDialogFocus'

export interface FormModalProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Called when the modal should close */
  onClose: () => void
  /** Modal title */
  title: string
  /** Optional modal description/subtitle */
  description?: string
  /** Optional icon to display next to title */
  icon?: ReactNode
  /** Icon background color class */
  iconBg?: string
  /** Icon color class */
  iconColor?: string
  /** Modal content (form fields, etc.) */
  children: ReactNode
  /** Footer content (buttons) - if not provided, no footer is rendered */
  footer?: ReactNode
  /** Max width class - defaults to max-w-lg */
  maxWidth?: string
  /** Z-index class — defaults to z-50, pass higher value to layer above other modals */
  zIndexClass?: string
  /** Force the dark modal treatment when rendered outside the authenticated theme shell. */
  forceDark?: boolean
}

export default function FormModal({
  isOpen,
  onClose,
  title,
  description,
  icon,
  iconBg = 'bg-blue-500/12',
  iconColor = 'text-blue-500',
  children,
  footer,
  maxWidth = 'max-w-lg',
  zIndexClass = 'z-50',
  forceDark = false,
}: FormModalProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const titleId = useId()
  const descriptionId = useId()
  const { dialogRef, onKeyDown } = useDialogFocus(isOpen && isVisible, onClose)

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true)
        })
      })
    } else {
      setIsAnimating(false)
      const timer = setTimeout(() => setIsVisible(false), 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  if (!isVisible) return null

  return createPortal(
    <div
      className={`
        fixed inset-0 ${zIndexClass} flex items-center justify-center p-4
        transition-opacity duration-[120ms]
        ${isAnimating ? 'opacity-100' : 'opacity-0'}
      `}
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[4px]" />

      {/* Modal */}
      <div
        ref={dialogRef as React.RefObject<HTMLDivElement>}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className={`
          relative w-full ${maxWidth}
          max-h-[90vh] overflow-y-auto
          ${forceDark ? 'bg-[#1B1C1F]' : 'bg-white dark:bg-[#1B1C1F]'}
          rounded-[15px]
          border ${forceDark ? 'border-white/5' : 'border-gray-200 dark:border-white/5'}
          shadow-[0_8px_32px_rgba(0,0,0,0.45)]
          transition-all duration-[140ms]
          ${isAnimating ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.96]'}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          aria-label="Close dialog"
          onClick={onClose}
          className={`absolute top-4 right-4 p-1 transition-colors z-10 ${forceDark ? 'text-white/40 hover:text-white/70' : 'text-gray-500 dark:text-white/40 hover:text-gray-700 dark:hover:text-white/70'}`}
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="pt-7 px-7 pb-4">
          <div className="flex items-center gap-3">
            {icon && (
              <div
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                  ${iconBg}
                `}
              >
                <span className={iconColor}>{icon}</span>
              </div>
            )}
            <div>
              <h2 id={titleId} className={`text-[18px] font-semibold ${forceDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                {title}
              </h2>
              {description && (
                <p id={descriptionId} className={`text-[14px] mt-0.5 ${forceDark ? 'text-white/50' : 'text-gray-500 dark:text-white/50'}`}>{description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-7 pb-6">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-7 pb-6 flex items-center justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

// Re-usable button styles for form modals
export function FormModalCancelButton({
  onClick,
  disabled = false,
  children = 'Cancel'
}: {
  onClick: () => void
  disabled?: boolean
  children?: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="px-4 py-2 text-[14px] font-medium text-gray-700 dark:text-white/75 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50"
    >
      {children}
    </button>
  )
}

export function FormModalSubmitButton({
  type = 'submit',
  onClick,
  disabled = false,
  loading = false,
  variant = 'primary',
  children = 'Save'
}: {
  type?: 'submit' | 'button'
  onClick?: () => void
  disabled?: boolean
  loading?: boolean
  variant?: 'primary' | 'success'
  children?: ReactNode
}) {
  const bgClass = variant === 'success'
    ? 'bg-green-600 hover:bg-green-500'
    : 'bg-blue-600 hover:bg-blue-500'

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        px-5 py-2.5 text-[14px] font-medium text-white rounded-lg
        transition-colors disabled:opacity-50
        ${bgClass}
      `}
    >
      {loading ? 'Processing...' : children}
    </button>
  )
}
